'use client';

import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import {
  readTappedThumbnail,
  rememberGiftPreview,
} from '@/app/_components/GiftCard/giftPreview';
import { Image } from '@/components/client';
import { Rating } from '@/components/molecule/Rating';
import { BREAKPOINTS, DONATION_ROUTES } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface RecentOrderGiftListItemProps {
  mainImageUrl: string;
  institutionName: string;
  productNumber: string;
  slugId: string;
  productName: string;
  reviewCount: number;
  reviewRating: number;
  createAt: string;
  priority?: boolean;
}

export const RecentOrderGiftListItem = ({
  mainImageUrl,
  institutionName,
  productNumber,
  slugId,
  productName,
  reviewCount,
  reviewRating,
  createAt,
  priority,
}: RecentOrderGiftListItemProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const timeAgo = getTimeAgo(createAt);

  return (
    <li>
      <CustomLink
        href={DONATION_ROUTES.GIFTS_DETAIL.pathname({
          productNumber: slugId ?? productNumber,
        })}
        aria-label={`${productName} 상세 페이지로 이동`}
        onClick={(event) => {
          rememberGiftPreview({
            productNumber: slugId ?? productNumber,
            mainImageUrl,
            thumbnailSrc: readTappedThumbnail(event.currentTarget),
            productName,
            institutionName,
          });
        }}
        className={cn(
          'group flex w-[22rem] items-center justify-between gap-20 rounded-xlarge',
          'transition-opacity duration-200 ease-out',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500',
          'active:opacity-80',
          'mobile-tablet:w-[20.9375rem] mobile-tablet:justify-start mobile-tablet:gap-16 mobile-tablet:pr-32',
        )}
      >
        <div
          className={cn(
            'relative size-140 shrink-0 overflow-hidden rounded-xlarge bg-Gray-black/10',
            'mobile-tablet:size-104',
          )}
        >
          <Image
            src={mainImageUrl}
            alt={productName}
            width={140 * 2}
            height={140 * 2}
            preload={priority}
            sizes={`${BREAKPOINTS.MOBILE_TABLET} 104px, 140px`}
            className="size-full object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-Gray-black/0 transition-colors duration-300 ease-out group-hover:bg-Gray-black/5"
          />
        </div>

        <div className="flex flex-1 flex-col">
          <p
            className={cn(
              'font-600 text-Gray-600 typo-body4',
              'mobile-tablet:font-400 mobile-tablet:typo-caption1',
            )}
          >
            {institutionName}
          </p>

          <p
            className={cn(
              'line-clamp-2 min-h-[2lh] pt-2 font-500 text-Gray-800 typo-body2',
              'mobile-tablet:font-400 mobile-tablet:typo-body4',
            )}
          >
            {productName}
          </p>

          <div className="flex items-center gap-4">
            <Rating value={reviewRating} size={isDesktop ? 16 : 12} />
            <span
              className={cn(
                'pt-6 font-400 text-Gray-600 typo-caption1',
                'mobile-tablet:typo-caption2',
              )}
            >
              ({reviewCount?.toLocaleString()})
            </span>
          </div>

          <p
            className={cn(
              'whitespace-nowrap pt-2 font-600 text-O-400 typo-body3',
              'mobile-tablet:font-400 mobile-tablet:typo-caption1',
            )}
          >
            {timeAgo} 전에 주문했어요!
          </p>
        </div>
      </CustomLink>
    </li>
  );
};

export const getTimeAgo = (date: string) => {
  if (typeof date !== 'string') return '-';

  //* 현재 시간과 주어진 날짜를 사용자의 로컬 시간대에서 계산
  const now = new Date();
  //* 입력으로 받는 iso는 한국 시간이므로 국제 표준시(utc)로 변경하려면 offset을 붙여줘야 함
  const targetDate = new Date(date + '+09:00');

  //* 시간 차이 계산 (밀리초)
  const diff = now.getTime() - targetDate.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  //* 음수인 경우 (미래 날짜) 처리
  if (seconds < 0) {
    return '방금';
  }

  if (seconds < 60) {
    return `${seconds}초`;
  }

  if (minutes < 60) {
    return `${minutes}분`;
  }

  return `${hours}시간`;
};
